import * as React from "react";
import { useCallback, useMemo } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import styled from "styled-components";
import moment from "moment";
import Spinner from "components/common/Spinner";
import AggregationWidgetConfig from "views/logic/aggregationbuilder/AggregationWidgetConfig";
import Pivot, { DateType } from "views/logic/aggregationbuilder/Pivot";
import Series from "views/logic/aggregationbuilder/Series";
import { fetchEventsHistogram, parseTypeFilter } from "components/events/fetchEvents";
import FullSizeContainer from "views/components/aggregationbuilder/FullSizeContainer";
import InteractiveContext from "views/components/contexts/InteractiveContext";
import PlotLegend from "views/components/visualizations/PlotLegend";
import GenericPlot from "views/components/visualizations/GenericPlot";
import useUserDateTime from "hooks/useUserDateTime";
import { toUTCFromTz, adjustFormat } from "util/DateTime";
import { isTypeRelativeWithStartOnly } from "views/typeGuards/timeRange";
import useOnRefresh from "components/common/PaginatedEntityTable/useOnRefresh";
const config = AggregationWidgetConfig.builder().visualization("line").rowPivots([Pivot.create(["timestamp"], DateType)]).columnPivots([Pivot.createValues(["type"])]).series([Series.forFunction("count()")]).rollup(false).build();
const height = 180;
const GraphContainer = styled.div`
  height: ${height}px;
  width: 100%;
  margin: 20px 0;
`;
const generateChart = (type, buckets) => {
  const x = buckets.map((b) => b.start_date);
  const y = buckets.map((b) => b.count);
  return {
    type: "scatter",
    name: type,
    x,
    y,
    originalName: type,
    line: {
      shape: "linear",
      color: type === "Alerts" ? "#4478b3" : "#fd9e48"
    },
    yaxis: "y",
    fullPath: `${type}\u2E31count()`
  };
};
const yLegendPosition = (containerHeight) => {
  if (containerHeight < 150) {
    return -0.6;
  }
  if (containerHeight < 400) {
    return -0.2;
  }
  return -0.14;
};
const defaultSetColor = (chart, colors) => {
  var _a;
  return {
    line: { color: colors.get((_a = chart.originalName) != null ? _a : chart.name) }
  };
};
const layout = {
  yaxis: {
    fixedrange: true,
    rangemode: "tozero",
    tickformat: ",~r",
    type: "linear"
  },
  dragmode: "zoom",
  hovermode: "x",
  xaxis: {
    domain: [0, 1],
    type: "date"
  },
  legend: { y: yLegendPosition(height) }
};
const prepareTimeRangeForGraph = (timerange) => {
  if (isTypeRelativeWithStartOnly(timerange)) {
    const from = moment().utc().subtract(timerange.range, "seconds");
    const to = moment();
    return [adjustFormat(from, "internal"), adjustFormat(to, "internal")];
  }
  return [timerange.from, timerange.to];
};
const EventsGraph = ({
  data: { results, timerange },
  alerts,
  onZoom
}) => {
  const chartData = useMemo(
    () => [
      ...["include", "exclude"].includes(alerts) ? [generateChart("Events", results.buckets.events)] : [],
      ...["include", "only"].includes(alerts) ? [generateChart("Alerts", results.buckets.alerts)] : []
    ],
    [alerts, results.buckets.alerts, results.buckets.events]
  );
  const _layout = useMemo(
    () => ({
      ...layout,
      xaxis: {
        ...layout.xaxis,
        range: prepareTimeRangeForGraph(timerange)
      }
    }),
    [timerange]
  );
  return /* @__PURE__ */ React.createElement(GraphContainer, null, /* @__PURE__ */ React.createElement(InteractiveContext.Provider, { value: false }, /* @__PURE__ */ React.createElement(FullSizeContainer, null, (dimensions) => /* @__PURE__ */ React.createElement(PlotLegend, { config, chartData, height, width: dimensions.width }, /* @__PURE__ */ React.createElement(InteractiveContext.Provider, { value: true }, /* @__PURE__ */ React.createElement(GenericPlot, { chartData, layout: _layout, onZoom, setChartColor: defaultSetColor }))))));
};
const EventsHistogram = ({ searchParams, setFilters, eventsHistogramFetcher = fetchEventsHistogram }) => {
  var _a, _b;
  const { userTimezone, formatTime } = useUserDateTime();
  const { data, isInitialLoading, refetch } = useQuery({
    queryKey: ["events", "histogram", searchParams],
    queryFn: () => eventsHistogramFetcher(searchParams),
    placeholderData: keepPreviousData
  });
  useOnRefresh(refetch);
  const alerts = parseTypeFilter((_b = (_a = searchParams == null ? void 0 : searchParams.filters) == null ? void 0 : _a.get("alert")) == null ? void 0 : _b[0]);
  const onZoom = useCallback(
    (from, to) => {
      const parsedFrom = formatTime(toUTCFromTz(from, userTimezone), "internal");
      const parsedTo = formatTime(toUTCFromTz(to, userTimezone), "internal");
      setFilters(searchParams.filters.set("timestamp", [`${parsedFrom}><${parsedTo}`]));
    },
    [formatTime, searchParams.filters, setFilters, userTimezone]
  );
  return isInitialLoading ? /* @__PURE__ */ React.createElement(Spinner, null) : /* @__PURE__ */ React.createElement(EventsGraph, { data, alerts, onZoom });
};
export default EventsHistogram;
